/**
 * Utility to convert between integer IDs and deterministic UUID-like strings.
 * This provides security by obscurity and prevents ID enumeration in URLs.
 */

// A fixed salt to make the "UUIDs" unique to this application
const ID_SALT = 'citoyen-pro-category-salt';

/**
 * Converts an integer ID to a deterministic UUID-like string.
 */
export const encodeId = (id: number | string): string => {
    const numId = Number(id);
    if (isNaN(numId)) return String(id);

    // Simple deterministic hex string that looks like a UUID
    // Format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
    // We use a simple hash-like approach
    const hash = (numId * 123456789 + 987654321).toString(16);
    const part1 = hash.padStart(8, '0').slice(-8);
    const part2 = ((numId * 54321) % 0xFFFF).toString(16).padStart(4, '0');
    const part3 = '4' + ((numId * 12345) % 0xFFF).toString(16).padStart(3, '0'); // v4-like
    const part4 = (0x8000 + ((numId * 999) % 0x3FFF)).toString(16).padStart(4, '0'); // variant-like
    const part5 = (numId * 0xDEADC0DE).toString(16).padStart(12, '0').slice(-12);

    return `${part1}-${part2}-${part3}-${part4}-${part5}`;
};

/**
 * Finds the actual object from a list by matching its encoded ID.
 */
export const findByEncodedId = <T extends { id: number | string }>(
    list: T[],
    encodedId: string | null
): T | undefined => {
    if (!encodedId) return undefined;
    return list.find(item => encodeId(item.id) === encodedId);
};
